import { useState } from 'react';
import { motion, useTransform } from 'framer-motion';
import { Scene, Counter, useSpan } from '../motion';
import PlayingCard from '../components/PlayingCard';
import { PrimaryButton, QuietButton } from '../components/Buttons';
import { LOG_LINES, PARSED_HAND } from '../demoData';

// Scanner timing: line i is read at lineAt(i) of scene progress.
const FIRST = 0.06;
const LAST = 0.78;
const STEP = (LAST - FIRST) / (LOG_LINES.length - 1);
const lineAt = i => FIRST + i * STEP;
const LINE_EM = 2.05;

function LogLine({ p, index, line }) {
  const at = lineAt(index);
  // dim -> highlighted while being read -> parsed
  const bg = useTransform(p, [at - STEP * 0.5, at, at + STEP * 0.9, at + STEP * 1.4], [
    'rgb(var(--pn-signal) / 0)', 'rgb(var(--pn-signal) / 0.14)', 'rgb(var(--pn-signal) / 0.14)', 'rgb(var(--pn-signal) / 0)',
  ]);
  const opacity = useTransform(p, [at - STEP * 2, at - STEP * 0.5, at], [0.38, 0.6, 1]);
  const tick = useTransform(p, [at, at + STEP * 0.6], [0, 1]);

  return (
    <motion.div
      className="flex items-center gap-[1em] whitespace-nowrap rounded-[0.4em] px-[0.75em]"
      style={{ height: `${LINE_EM}em`, backgroundColor: bg, opacity }}
    >
      <span className="hidden text-pn-muted sm:inline">{line.t}</span>
      <span className="min-w-0 flex-1 overflow-hidden text-ellipsis">{line.text}</span>
      {line.field && (
        <motion.span className="text-pn-signal" style={{ opacity: tick }} aria-hidden="true">
          ✓
        </motion.span>
      )}
    </motion.div>
  );
}

function LogPanel({ p }) {
  // Phones show fewer lines; fixed per viewport so frames stay deterministic.
  const [visible] = useState(() => (window.matchMedia('(min-width: 640px)').matches ? 9 : 6));
  const scanned = useTransform(p, [FIRST, LAST], [0, LOG_LINES.length - 1], { clamp: true });
  const maxShift = LOG_LINES.length - visible;
  const lead = Math.floor(visible / 2);
  const y = useTransform(scanned, v => `${-Math.min(maxShift, Math.max(0, v - lead)) * LINE_EM}em`);

  return (
    <div className="pn-bezel">
      <div className="pn-plate overflow-hidden">
        <div className="flex items-center justify-between border-b border-pn-line/10 px-4 py-3">
          <span className="pn-caption">poker_now_log_thursday.csv</span>
          <span className="pn-caption">full log</span>
        </div>
        <div
          className="relative overflow-hidden px-2 py-2 font-mono text-[clamp(0.62rem,0.3rem+1*var(--u),1.3rem)]"
          style={{ height: `calc(${visible * LINE_EM}em + 1rem)` }}
        >
          <motion.div style={{ y }}>
            {LOG_LINES.map((line, i) => (
              <LogLine key={i} p={p} index={i} line={line} />
            ))}
          </motion.div>
        </div>
      </div>
    </div>
  );
}

// A value that fades in when its log line is read; shows a skeleton before.
function Field({ p, line, children, className = '' }) {
  const t = useSpan(p, lineAt(line), lineAt(line) + STEP * 0.9);
  const y = useTransform(t, [0, 1], [10, 0]);
  const skeleton = useTransform(t, [0, 0.6], [1, 0]);
  return (
    <span className={`relative inline-flex ${className}`}>
      <motion.span
        aria-hidden="true"
        className="absolute inset-0 rounded-md bg-pn-line/10"
        style={{ opacity: skeleton }}
      />
      <motion.span className="inline-flex gap-[0.3em]" style={{ opacity: t, y }}>
        {children}
      </motion.span>
    </span>
  );
}

function HandCard({ p }) {
  const h = PARSED_HAND;
  // Steps up as each of your bets is read, holding between them.
  const betLines = [4, 7, 10, 13];
  const betTotals = [1.5, 3.75, 8.75, 18.5];
  const invested = useTransform(
    p,
    [0, ...betLines.flatMap(l => [lineAt(l), lineAt(l) + STEP * 0.6])],
    [0, ...betTotals.flatMap((v, i) => [i ? betTotals[i - 1] : 0, v])],
  );
  const collected = useTransform(p, [lineAt(15), lineAt(16)], [0, h.collected]);
  const net = useTransform(p, [lineAt(16), lineAt(17)], [0, h.net]);
  const netShow = useSpan(p, lineAt(16), lineAt(17));

  return (
    <div className="pn-bezel shadow-[0_40px_80px_-40px_rgb(var(--pn-sunken))]">
      <div className="pn-plate p-[clamp(1rem,0.6rem+1.4*var(--u),1.75rem)]">
        <div className="flex items-baseline justify-between">
          <p className="pn-h3">
            Hand <Field p={p} line={0}>#{h.number}</Field>
          </p>
          <span className="pn-caption">parsed</span>
        </div>

        <div className="mt-[clamp(0.8rem,0.5rem+1*var(--u),1.4rem)] grid grid-cols-[auto_1fr] items-center gap-x-5 gap-y-3">
          <span className="pn-caption">You</span>
          <Field p={p} line={3}>
            {h.hole.map(c => <PlayingCard key={c} card={c} />)}
          </Field>
          <span className="pn-caption">Board</span>
          <span className="flex gap-[0.3em]">
            <Field p={p} line={6}>{h.flop.map(c => <PlayingCard key={c} card={c} />)}</Field>
            <Field p={p} line={9}><PlayingCard card={h.turn} /></Field>
            <Field p={p} line={12}><PlayingCard card={h.river} /></Field>
          </span>
          <span className="pn-caption">{h.opponent.name}</span>
          <Field p={p} line={15}>
            {h.opponent.cards.map(c => <PlayingCard key={c} card={c} />)}
          </Field>
        </div>

        <div className="mt-[clamp(0.9rem,0.5rem+1.2*var(--u),1.6rem)] grid grid-cols-3 gap-3 border-t border-pn-line/10 pt-4">
          <div>
            <p className="pn-caption">Invested</p>
            <p className="pn-num mt-1 text-[clamp(1rem,0.7rem+1*var(--u),1.9rem)]">
              $<Counter value={invested} format={v => v.toFixed(2)} />
            </p>
          </div>
          <div>
            <p className="pn-caption">Collected</p>
            <p className="pn-num mt-1 text-[clamp(1rem,0.7rem+1*var(--u),1.9rem)]">
              $<Counter value={collected} format={v => v.toFixed(2)} />
            </p>
          </div>
          <motion.div style={{ opacity: useTransform(netShow, [0, 1], [0.35, 1]) }}>
            <p className="pn-caption">Net</p>
            <p className="pn-num mt-1 text-[clamp(1rem,0.7rem+1*var(--u),1.9rem)] font-bold text-pn-signal">
              +$<Counter value={net} format={v => v.toFixed(2)} />
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default function Hero({ onStart, onExample }) {
  return (
    <Scene id="top" length={2.6} label="Introduction">
      {p => <HeroFrame p={p} onStart={onStart} onExample={onExample} />}
    </Scene>
  );
}

function HeroFrame({ p, onStart, onExample }) {
  const copyY = useTransform(p, [0.86, 1], ['0%', '-6%']);
  const glow = useTransform(p, [0, 0.8], [0.35, 1]);

  return (
    <div className="relative mx-auto flex h-full max-w-[1480px] flex-col px-4 pb-6 pt-[5.5rem] sm:px-6 tall:justify-center tall:px-12 land:grid land:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] land:items-center land:gap-16 land:pb-10 land:pt-24">
      <motion.div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-[55%] -z-10 h-[70*var(--u)] w-[90*var(--u)] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(closest-side,rgb(var(--pn-glow)/0.16),transparent)] land:left-[72%]"
        style={{ opacity: glow }}
      />

      <motion.div style={{ y: copyY }} className="relative">
        <h1 className="pn-display max-w-[12ch]">Your home game, read back to you.</h1>
        <p className="pn-lede mt-[clamp(1rem,0.5rem+1.6*var(--u),2rem)]">
          Drop in a PokerNow log. Get every hand parsed, every player&rsquo;s VPIP, PFR and 3-bet, and who owes who.
        </p>
        <div className="mt-[clamp(1.25rem,0.6rem+2*var(--u),2.5rem)] flex flex-wrap items-center gap-3">
          <PrimaryButton onClick={onStart} className="text-[clamp(0.95rem,0.85rem+0.4*var(--u),1.15rem)]">Start free</PrimaryButton>
          <QuietButton onClick={onExample} className="text-[clamp(0.95rem,0.85rem+0.4*var(--u),1.15rem)]">See an example</QuietButton>
        </div>
      </motion.div>

      <div className="relative mt-[clamp(1.25rem,1rem+2*var(--u),3rem)] min-h-0 flex-1 tall:mt-[6vh] tall:flex-none land:mt-0 land:flex-none">
        <div className="relative mx-auto w-full max-w-[min(100%,46rem)] tall:max-w-none">
          <div className="w-[94%] land:w-[92%]">
            <LogPanel p={p} />
          </div>
          <div className="relative z-10 -mt-10 ml-auto w-[90%] tall:-mt-[9%] tall:w-[86%] land:-mt-[22%] land:w-[74%]">
            <HandCard p={p} />
          </div>
        </div>
      </div>
    </div>
  );
}
